define('home/views/NavView',[
    'underscore',
    'jquery',
    'backbone-marionette',

    'core/UniqueModel',

    'home/utils/confirmModal',

    'home/models/Channel',
    'home/models/Session',
    'home/models/ChannelProfileFeed',

    'home/templates/nav',
    'home/templates/anonNav',
    'home/templates/createDiscussionNav',

    'home/mixins/withClickLinkTracking',
    'home/mixins/withOnboarding',
    'home/mixins/withSubviews',
], function (
    _,
    $,
    Marionette,

    UniqueModel,

    confirmModal,

    Channel,
    Session,
    ChannelProfileFeed,

    navTemplate,
    anonNavTemplate,
    createDiscussionNavTemplate,

    withClickLinkTracking,
    withOnboarding,
    withSubviews
) {
    'use strict';

    var parent = Marionette.ItemView;
    var parentProto = parent.prototype;

    var NavView = parent.extend({
        className: 'compensation',

        template: function (data) {
            if (data.id)
                return navTemplate(data);

            return anonNavTemplate(data);
        },

        templateHelpers: function () {
            return {
                // For development
                customBase: this.devBase,
                // For anon nav template
                sessionIsFetching: this.session.fetchPromise,

                stepNumbers: _.range(1, this.constructor.NUM_STEPS + 1),
                userCanSkipOnboarding: this.userCanSkip(),
                onboardingWithoutChannels: this.app.onboardingWithoutChannels,
            };
        },

        events: {
            'show.bs.dropdown [data-action=toggle-dropdown]': 'removeTooltip',
            'hide.bs.dropdown [data-action=toggle-dropdown]': 'addTooltip',
            'click [data-role=settings] a[data-link-name]': 'trackClickLinkWithArea',
            'click [data-action=navigate-return]': 'navigateAndReturn',
            'click [data-action=create-discussion]': 'handleCreateDiscussionNavClick',
        },

        initialize: function (options) {
            this.session = Session.get();
            this.app = options.app;

            this.notifications = this.session.getOrCreateNotifications();

            this.promotedChannels = new UniqueModel(ChannelProfileFeed, {
                listName: ChannelProfileFeed.LIST_NAMES.promoted,
            });
            this.promotedChannels.ensureFetched();

            if (!this.session.user.get('isAnonymous'))
                this.changeUser();
            this.listenTo(
                this.session,
                'change:id fetch:error fetch:success change:isModerator',
                this.changeUser
            );
            this.listenTo(this.notifications.unreadCounter, 'change:numUnread', this.updateNotifications);
            this.listenTo(this.app, 'route', this.activatePageIcon);
            this.listenTo(this.app, 'route', this.toggleIconLinks);

            this.listenTo(this.app, 'route', this.updateOnboardingSteps);
            this.listenTo(this, 'change:stepNumber', this.updateOnboardingSteps);

            this.devBase = $('body').attr('data-disqus-dev-base');
        },

        /**
         * Changes the model being used
         * Meant to be used in an event listener, listening to changes to `this.session.user`
         * @returns {void}
         */
        changeUser: function () {
            // Remove the listener before attaching the same one because we only want to keep a single listener
            this.stopListening(this.model, 'change:username', this.debouncedRender);
            this.model = this.session.user;
            this.listenTo(this.model, 'change:username', this.debouncedRender);
            this.debouncedRender();
        },

        debouncedRender: _.debounce(function () {
            this.render.apply(this, arguments);
        }, 300),

        render: function () {
            var result = parentProto.render.apply(this, arguments);
            this.updateOnboardingSteps();
            this.updateNotifications();
            return result;
        },

        onDomRefresh: function () {
            $('.tooltipped').tooltip();
        },

        updateNotifications: function () {
            var numUnread = this.notifications.unreadCounter.get('numUnread');
            this.$('[data-role=notifications]').toggleClass('has-notifs', Boolean(numUnread));
            this.$('[data-role=unread-notification-count]').text(numUnread > 9 ? '9+' : numUnread);
        },

        /**
         * Navigates to the href specified by the anchor element that was clicked.
         * Adds/modifies the next url param to return to this url when complete.
         *
         * @param {Event} evt - Click event object
         */
        navigateAndReturn: function (evt) {
            if (!evt)
                return;

            evt.preventDefault();

            var url = $(evt.currentTarget).attr('href');
            if (!url)
                return;

            var regex = /(\?|&)next=[^&]+/;
            var nextParam = 'next=' + encodeURIComponent('https://disqus.com' + window.location.pathname);
            var matched = false;

            url = url.replace(regex, function (_match, p1) {
                // An existing url param next was found. Replace it with the current path,
                // and mark the matched flag as true.
                matched = true;
                return p1 + nextParam;
            });

            if (!matched) {
                // No url param next was found. Add it.
                if (url.indexOf('?') === -1)
                    url += '?';
                else if (url.charAt(url.length - 1) !== '&')
                    url += '&';

                url += nextParam;
            }

            window.top.location = url;
        },

        /**
         * Returns a list of channels to suggest to the user for creating
         * a new discussion.
         * This includes promoted channels, the channels the user is following,
         * and the channel for the page the user is on, if any.
         *
         * @returns {Promise} - List of channels wrapped in a promise
         */
        getDiscussChannels: function () {
            return this.promotedChannels.ensureFetched().then(_.bind(function () {
                // Promoted channels
                var promotedChannels = this.promotedChannels.items.map(function (channelProfile) {
                    return channelProfile.channel;
                });

                // Channels followed by the session user (may contain nulls)
                var sessionUserProfile = this.session.getUserProfile();
                var followedChannels = _.pluck(sessionUserProfile.forumsFollowing.items.models, 'channel');

                // Current page channel (if any, otherwise null)
                var currentViewModel = this.app.getCurrentView().model || {};
                var viewChannel = currentViewModel instanceof Channel ?
                    currentViewModel :
                    currentViewModel.channel;

                // we need to ensure channel is a real channel (has an id), since
                // some views can have a new channel without id (channel create view)
                if (viewChannel && !viewChannel.id)
                    viewChannel = null;

                var sessionUser = this.session.user;

                // Combined list of channels to pass to the modal, removes nulls
                return _.chain([viewChannel, promotedChannels, followedChannels])
                    .flatten()
                    .compact()
                    .filter(function (channel) {
                        return channel.userCanCreate(sessionUser);
                    })
                    .uniq(false, function (channel) {
                        return channel.id;
                    })
                    .invoke('toJSON')
                    .value();
            }, this));
        },

        /**
         * Opens a modal with a list of links to the discussion creation page for
         * various channels.
         * List of channels includes promoted channels, channels followed by the
         * current user, and the channel for the current page, if any.
         * Curation-only channels are not shown.
         */
        handleCreateDiscussionNavClick: function () {
            this.getDiscussChannels().then(function (combined) {
                var model = {
                    channels: combined,
                };
                confirmModal.confirmationPromise(
                    confirmModal.TYPES.CLOSE_LIGHT,
                    createDiscussionNavTemplate(model)
                ).then(function (navigated) {
                    // Make sure all modals are removed when
                    // navigating to a new create discussion page.
                    //
                    // All of the links in the channel selection
                    // modal should have `data-action="confirm"`,
                    // which will resolve this promise to `true`.
                    if (navigated)
                        $('.modal').remove();
                });
            }, this);
        },

        /**
         * Updates the active state of any page icons in this view.
         * The only page icon that should be active is one that is linked to the current route.
         * The active state typically adjusts the icon's style to visually indicate which page
         * is currently visible to the user.
         *
         * @param {string} routeName - Name of the active page
         * @param {Array} routeArgs - Url arguments
         */
        activatePageIcon: function (routeName, routeArgs) {
            // If the page rerenders after this is called, the element lose its active class.
            // If that happens, need to reactivate the element using the current arguments (the
            // most recent route name and args).
            // Store the reactivation handler so it can be removed before another is added,
            // at most 1 handler will ever be bound.
            if (this.reactivatePageIcon)
                this.stopListening(this, 'render', this.reactivatePageIcon);

            this.reactivatePageIcon = _.bind(this.activatePageIcon, this, routeName, routeArgs);
            this.listenTo(this, 'render', this.reactivatePageIcon);

            // Remove any previously activated icons, then activate the one matching the current
            // route.
            this.$('[data-page-icon].active').removeClass('active');

            // Special case for profile to only highlight if viewing the session user's profile
            if (routeName !== 'profile' || routeArgs[0] === this.session.user.id)
                this.$('[data-page-icon~="' + routeName + '"]').addClass('active');
        },

        toggleIconLinks: function (routeName) {
            if (routeName === 'home' && this.session.isLoggedOut())
                $('[data-role=icon-links]').hide();
            else
                $('[data-role=icon-links]').show();
        },

        /**
         * If current route is an onboarding route, apply the is-onboarding class to the nav view and
         * update the active/completed state of the onboarding steps.
         */
        updateOnboardingSteps: function () {
            // defer so that onboarding transitions are the last to occur and can be seen
            _.defer(_.bind(function () {
                var inOnboarding = this.isInOnboarding(this.app, this.session);

                this.$('[data-role=onboarding-steps-icon]').removeClass('is-complete');
                this.$el.toggleClass('is-onboarding', inOnboarding);

                if (!inOnboarding)
                    return;

                // mark all previous steps as completed, if any
                var stepNumber = this.getStepNumber();
                _.each(_.range(1, stepNumber), function (step) {
                    this.$('[data-role=onboarding-steps-icon][data-onboarding-step=' + step + ']')
                        .addClass('is-complete');
                }, this);
            }, this));
        },

        completeHomeOnboarding: function () {
            this.session.user.setHomeOnboardComplete(true);
        },

        removeTooltip: function (evt) {
            if (!evt)
                return;

            $(evt.currentTarget).closest('.tooltipped').tooltip('destroy');
        },

        addTooltip: function (evt) {
            if (!evt)
                return;

            $(evt.currentTarget).closest('.tooltipped').tooltip();
        },
    });


    var proto = NavView.prototype;
    withClickLinkTracking.call(proto, 'top_nav_user');
    withSubviews.call(proto);
    withOnboarding.call(proto);

    return NavView;
});

